package org.example.project.screens_sys

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonObject
import org.example.project.manager.LumaticaApiManager
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlinx.coroutines.launch
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

private val zweiFaktorSettingsJson = Json { ignoreUnknownKeys = true }

private fun zweiFaktorDefaultEntries(): Map<String, Boolean> =
    mapOf("SYS" to true, "NET" to false, "CUS" to false, "DRIVER" to false)

private val ZWEI_FAKTOR_ENTRY_INFO = listOf(
    All(key = "SYS", label = "SYS"),
    All(key = "NET", label = "NET"),
    All(key = "CUS", label = "CUS"),
    All(key = "DRIVER", label = "Fahrer")
)

private suspend fun loadZweiFaktorSettings(): JsonObject {
    val raw = window.fetch("/api/zweifaktor/settings").await().text().await()
    return runCatching { zweiFaktorSettingsJson.parseToJsonElement(raw).jsonObject }.getOrNull() ?: JsonObject(emptyMap())
}

private suspend fun saveZweiFaktorSettings(
    entries: Map<String, Boolean>,
    trustedDeviceDays: Int,
    maxTrustedDevices: Int,
    allowTrustedDevices: Boolean
): JsonObject {
    val response = window.fetch(
        "/api/zweifaktor/settings-save",
        RequestInit(
            method = "POST",
            headers = LumaticaApiManager.auditHeaders("application/json"),
            body = buildJsonObject {
                put("entries", buildJsonObject {
                    entries.forEach { (key, active) -> put(key, JsonPrimitive(active)) }
                })
                put("trustedDeviceDays", JsonPrimitive(trustedDeviceDays))
                put("maxTrustedDevices", JsonPrimitive(maxTrustedDevices))
                put("allowTrustedDevices", JsonPrimitive(allowTrustedDevices))
            }.toString()
        )
    ).await()
    val root = runCatching { zweiFaktorSettingsJson.parseToJsonElement(response.text().await()).jsonObject }.getOrNull()
    return if (response.ok && root?.get("ok")?.jsonPrimitive?.booleanOrNull == true) loadZweiFaktorSettings() else JsonObject(emptyMap())
}

@Composable
fun SysZweiFaktorVerwaltung() {
    Style(cssRules = SharedWorkspaceStyle.cssRules)

    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var entries by remember { mutableStateOf(zweiFaktorDefaultEntries()) }
    var trustedDeviceDays by remember { mutableStateOf(10) }
    var maxTrustedDevices by remember { mutableStateOf(5) }
    var allowTrustedDevices by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current

    suspend fun reloadSettings() {
        val root = loadZweiFaktorSettings()
        val loadedEntries = root["entries"]?.jsonObject ?: JsonObject(emptyMap())
        entries = zweiFaktorDefaultEntries().mapValues { (key, fallback) ->
            loadedEntries[key]?.jsonPrimitive?.booleanOrNull ?: fallback
        }
        trustedDeviceDays = root["trustedDeviceDays"]?.jsonPrimitive?.intOrNull ?: 10
        maxTrustedDevices = root["maxTrustedDevices"]?.jsonPrimitive?.intOrNull ?: 5
        allowTrustedDevices = root["allowTrustedDevices"]?.jsonPrimitive?.booleanOrNull ?: true
        loading = false
    }

    LaunchedEffect(Unit) {
        reloadSettings()
    }

    Div({ classes(SharedWorkspaceStyle.workspace) }) {
        Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
            Div({
                classes(SharedWorkspaceStyle.contentInner)
                style {
                    maxWidth(100.percent)
                    flex(1)
                    minHeight(0.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                }
            }) {
                if (internalTitleVisible) {
                    Div({
                        classes(SharedWorkspaceStyle.rightTitle)
                        style { justifyContent(JustifyContent.Center) }
                    }) {
                        Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("2F Browser-Freigaben") }
                    }
                }

                Div({
                    classes(SharedWorkspaceStyle.actionBar)
                    style {
                        maxWidth(100.percent)
                        justifyContent(JustifyContent.Center)
                        property("grid-template-columns", "repeat(2, minmax(0, 160px))")
                    }
                }) {
                    LumaticaButton(
                        label = if (saving) "..." else "Speichern",
                        onClick = {
                            saving = true
                            scope.launch {
                                val root = saveZweiFaktorSettings(entries, trustedDeviceDays, maxTrustedDevices, allowTrustedDevices)
                                val loadedEntries = root["entries"]?.jsonObject ?: JsonObject(emptyMap())
                                if (loadedEntries.isNotEmpty()) {
                                    entries = zweiFaktorDefaultEntries().mapValues { (key, fallback) ->
                                        loadedEntries[key]?.jsonPrimitive?.booleanOrNull ?: fallback
                                    }
                                }
                                saving = false
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        enabled = !loading && !saving,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Aktualisieren",
                        onClick = { scope.launch { reloadSettings() } },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = !loading && !saving,
                        heightPx = 42
                    )
                }

                Div({ classes(SharedWorkspaceStyle.centeredDivider) })

                Div({
                    style {
                        flex(1)
                        minHeight(0.px)
                        overflowY("auto")
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(14.px)
                    }
                }) {
                    Div({
                        classes(SharedWorkspaceStyle.permissionHeader)
                        style { property("grid-template-columns", "minmax(260px, 1fr) 160px") }
                    }) {
                        Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text("Bereich") }
                        Span({ classes(SharedWorkspaceStyle.centeredHeader) }) { Text("2F erforderlich") }
                    }

                    ZWEI_FAKTOR_ENTRY_INFO.forEach { entry ->
                        ZweiFaktorEntryRow(
                            label = entry.label,
                            checked = entries[entry.key] == true,
                            enabled = !loading && !saving,
                            onChange = { checked -> entries = entries + (entry.key to checked) }
                        )
                    }
                    ZweiFaktorEntryRow(
                        label = "2F-Browser-Freigaben erlauben",
                        info = "Ein Browser kann per HttpOnly-Cookie wiedererkannt werden. Das Backend speichert dazu nur den passenden Hash-Eintrag.",
                        checked = allowTrustedDevices,
                        enabled = !loading && !saving,
                        onChange = { allowTrustedDevices = it }
                    )
                }
            }
        }
    }
}

@Composable
private fun ZweiFaktorEntryRow(label: String, info: String = "", checked: Boolean, enabled: Boolean, onChange: (Boolean) -> Unit) {
    Div({
        classes(SharedWorkspaceStyle.permissionRow)
        style {
            property("grid-template-columns", "minmax(260px, 1fr) 160px")
            alignItems(AlignItems.Center)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(2.px)
                minWidth(0.px)
            }
        }) {
            Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text(label) }
            if (info.isNotBlank()) {
                Span({ style { property("font-size", "12px"); property("opacity", "0.7") } }) { Text(info) }
            }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
            }
        }) {
            LumaticaCheckbox(checked = checked, onChange = onChange, enabled = enabled)
        }
    }
}
